import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { HiMenu, HiX } from 'react-icons/hi';
import { useAuth } from '../context/AuthContext';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { isAuthenticated, isAdmin } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const navLinks = [
    { to: '/', label: 'Home' },
    { to: '/specials', label: 'Specials' },
    { to: '/gallery', label: 'Gallery' },
    { to: '/catering', label: 'Catering' },
    { to: '/weddings', label: 'Weddings' },
    { to: '/faq', label: 'FAQ' },
    { to: '/blog', label: 'Blog' },
  ];

  const getLinkClass = ({ isActive }) =>
    `nav-link py-2 ${isActive ? 'nav-link-active' : ''}`;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || isOpen
          ? 'bg-piggy-bg/95 backdrop-blur-md shadow-lg shadow-black/20'
          : 'bg-transparent'
      }`}
    >
      <nav className="container mx-auto py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link
            to="/"
            className="text-2xl md:text-3xl font-display font-bold group"
          >
            <span className="text-white group-hover:text-piggy-pink transition-colors">
              The Piggy
            </span>
            <span className="text-piggy-pink"> BBQ</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navLinks.map((link) =>
              link.external ? (
                <a
                  key={link.to}
                  href={link.to}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="nav-link py-2"
                >
                  {link.label}
                </a>
              ) : (
                <NavLink key={link.to} to={link.to} className={getLinkClass} end={link.to === '/'}>
                  {link.label}
                </NavLink>
              )
            )}
            {isAuthenticated && isAdmin() && (
              <Link to="/admin" className="btn-primary text-sm">
                Admin
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-white p-2 hover:text-piggy-pink transition-colors"
            onClick={() => setIsOpen(!isOpen)}
            aria-label={isOpen ? 'Close menu' : 'Open menu'}
          >
            {isOpen ? <HiX size={28} /> : <HiMenu size={28} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="lg:hidden overflow-hidden"
            >
              <div className="flex flex-col space-y-4 py-6 border-t border-gray-700 mt-4">
                {navLinks.map((link) =>
                  link.external ? (
                    <a
                      key={link.to}
                      href={link.to}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-300 hover:text-piggy-pink transition-colors text-lg"
                    >
                      {link.label}
                    </a>
                  ) : (
                    <NavLink
                      key={link.to}
                      to={link.to}
                      className={({ isActive }) =>
                        `text-lg ${isActive ? 'text-piggy-pink' : 'text-gray-300 hover:text-piggy-pink'} transition-colors`
                      }
                      end={link.to === '/'}
                    >
                      {link.label}
                    </NavLink>
                  )
                )}
                {isAuthenticated && isAdmin() && (
                  <Link to="/admin" className="btn-primary text-center">
                    Admin Dashboard
                  </Link>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
};

export default Navbar;
